import Head from 'next/head';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import { useSelector } from 'react-redux';
import SEO from 'next-seo.config';
import { authSelector } from 'store/auth';
import SidebarLayout from 'layouts/SidebarLayout';
import Typo from 'components/Typo';
import BrokerCard from './BrokerCard';
import DirectoryStories from './DirectoryStories';
import DirectoryBreadcrumb from './DirectoryBreadcrumb';
import DirectoryNotes from './DirectoryNotes';
import {
    MORTGAGE_BROKERS_PATH,
    pageUpdatedAt,
    townCopy,
    townIntro,
    townPath,
    townSchema,
} from 'utils/directory';
import safeHtml, { jsonLd } from 'utils/safe-html';
import styles from 'styles/modules/BrokerDirectory.module.scss';

// A town page: every verified mortgage broker whose address is in the town,
// most third-party coverage first.
export default function TownPage({ page }) {
    const { isAuthenticated } = useSelector(authSelector);
    const { town, brokers, stories } = page;
    const { title, heading, description, intro } = townCopy(page);
    const path = townPath(town.slug);
    const url = `${process.env.HOST}${path}`;

    return (
        <SidebarLayout isAuthenticated={isAuthenticated} breadcrumb={false}>
            <NextSeo
                {...SEO}
                title={title}
                titleTemplate="%s"
                description={description}
                canonical={url}
                noindex={!page.indexable}
                openGraph={{ ...SEO.openGraph, title, description, url }}
            />
            <Head>
                <script
                    key="directory-jsonld"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: jsonLd(townSchema(page, process.env.HOST)),
                    }}
                />
            </Head>

            <div className={styles.wrapper}>
                <DirectoryBreadcrumb
                    items={[
                        ['Newspage', '/'],
                        ['Mortgage brokers', MORTGAGE_BROKERS_PATH],
                        [town.name, path],
                    ]}
                />
                <div className={styles.head}>
                    <Typo variant="h1">{heading}</Typo>
                    {intro ? (
                        <div
                            className={styles.intro}
                            dangerouslySetInnerHTML={{
                                __html: safeHtml(intro),
                            }}
                        />
                    ) : (
                        <p className={styles.intro}>{townIntro(page)}</p>
                    )}
                    <DirectoryNotes
                        updatedAt={pageUpdatedAt(page)}
                        order="Ordered by the number of pieces of media coverage, then by the most recent one."
                    />
                </div>

                <ul className={styles.list}>
                    {brokers.map((broker) => (
                        <BrokerCard key={broker.slug} broker={broker} />
                    ))}
                </ul>

                <DirectoryStories stories={stories} label={town.name} />

                <p className={styles.backLink}>
                    <Link href={MORTGAGE_BROKERS_PATH}>
                        <a>All mortgage broker towns and topics</a>
                    </Link>
                </p>
            </div>
        </SidebarLayout>
    );
}
